<template>
  <div class="ps-panel">
    <div class="ps-panel-hd">
      <h2>Segurança</h2>
      <p>Altere sua senha de acesso ao sistema.</p>
    </div>
    <div v-if="!changingPassword" class="ps-security-prompt">
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3">
        <rect x="6" y="14" width="20" height="14" rx="3"/><path d="M10 14v-4a6 6 0 0 1 12 0v4" stroke-linecap="round"/><circle cx="16" cy="21" r="2" fill="currentColor" stroke="none"/>
      </svg>
      <p>Sua senha está protegida. Clique em "Alterar senha" para defini-la.</p>
      <button class="btn btn-ghost btn-login" type="button" @click="openChangePassword">Alterar senha</button>
    </div>
    <div v-else class="ps-field-group">
      <div class="input-field">
        <label for="seg-curpw">Senha atual</label>
        <input id="seg-curpw" v-model="currentPasswordInput" type="password" />
      </div>
      <div class="input-field">
        <label for="seg-newpw">Nova senha</label>
        <input id="seg-newpw" v-model="newPassword" type="password" />
      </div>
      <div class="input-field">
        <label for="seg-confpw">Confirmar nova senha</label>
        <input id="seg-confpw" v-model="confirmNewPassword" type="password" />
      </div>
      <p v-if="error" class="ps-error">{{ error }}</p>
      <div class="ps-panel-ft">
        <button class="btn btn-primary btn-login" type="button" @click="changePassword">Alterar senha</button>
        <button class="btn btn-ghost btn-login" type="button" @click="cancelChangePassword">Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { accountsStore } from '@/store/accounts'
import { sessionStore } from '@/store/session'

export default defineComponent({
  name: 'ProfileSegurancaTab',
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const changingPassword = ref(false)
    const currentPasswordInput = ref('')
    const newPassword = ref('')
    const confirmNewPassword = ref('')
    const storedPassword = ref('')
    const error = ref('')

    onMounted(async () => {
      const session = sessionStore.state.user
      if (!session) return
      await accountsStore.refresh().catch(() => { /* ignore */ })
      const account = accountsStore.all().find(a => a.uid === session.uid || a.username === session.username)
      if (account) storedPassword.value = account.password || ''
    })

    const openChangePassword = () => {
      error.value = ''
      changingPassword.value = true
      currentPasswordInput.value = ''
      newPassword.value = ''
      confirmNewPassword.value = ''
    }

    const cancelChangePassword = () => {
      changingPassword.value = false
      currentPasswordInput.value = ''
      newPassword.value = ''
      confirmNewPassword.value = ''
      error.value = ''
    }

    const changePassword = async () => {
      error.value = ''
      const session = sessionStore.state.user
      if (!session) { router.push('/login'); return }
      if (!currentPasswordInput.value.trim() || !newPassword.value.trim() || !confirmNewPassword.value.trim()) {
        error.value = t('profile.passwordFillAll')
        return
      }
      if ((storedPassword.value || '') !== currentPasswordInput.value.trim()) {
        error.value = t('profile.passwordWrong')
        return
      }
      if (newPassword.value.trim() !== confirmNewPassword.value.trim()) {
        error.value = t('profile.passwordMismatch')
        return
      }
      await accountsStore.upsert({
        uid: session.uid,
        name: session.name,
        username: session.username,
        password: newPassword.value.trim(),
        role: session.role,
        roleId: session.roleId || '',
        subordinateUsernames: session.subordinateUsernames || [],
        hasSubordinates: Boolean(session.hasSubordinates),
        avatar: session.avatar
      })
      storedPassword.value = newPassword.value.trim()
      cancelChangePassword()
    }

    return {
      changingPassword, currentPasswordInput, newPassword, confirmNewPassword, error,
      openChangePassword, cancelChangePassword, changePassword,
    }
  }
})
</script>

<style scoped lang="scss">
.ps-panel-hd { margin-bottom: 24px; h2 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 4px; } p { font-size: 13px; color: var(--ink-muted); margin: 0; } }

.ps-security-prompt {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  padding: 32px; background: var(--card, #fff);
  border: 1px solid var(--border, rgba(31,30,26,.1)); border-radius: 12px; text-align: center;
  p { margin: 0; font-size: 14px; color: var(--ink-muted); max-width: 280px; }
}

.ps-field-group {
  display: flex; flex-direction: column; gap: 16px;
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1));
  border-radius: 12px; padding: 20px; margin-bottom: 4px;
}
.input-field {
  display: grid; gap: 5px;
  label { font-size: 13px; font-weight: 600; color: var(--ink-muted); }
  input { border: 1px solid var(--border-strong); border-radius: var(--radius, 6px); padding: 8px 10px; font-size: 14px; background: var(--surface); color: var(--ink); &:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-light); } }
}

.ps-error { font-size: 13px; color: #a54b2a; margin: 0; }
.ps-panel-ft { display: flex; gap: 8px; padding-top: 4px; }
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid transparent; border-radius: var(--radius, 6px); padding: 6px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .12s; }
.btn-primary { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); &:hover { filter: brightness(1.1); } }
.btn-ghost { background: transparent; color: var(--ink); border-color: var(--border-strong); &:hover { background: var(--surface-3); } }
.btn-login { padding: 10px 22px; border-radius: 8px; }
</style>
